import { supabase } from './supabase';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8787';
const API_URL = `${API_BASE}/api/v1`;

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  code?: string;
}

/**
 * Check if a JWT token is expired or about to expire (within 60 seconds)
 */
function isTokenExpired(token: string): boolean {
  try {
    // JWT format: header.payload.signature
    const payload = token.split('.')[1];
    if (!payload) return true;

    // Decode base64 payload
    const decoded = JSON.parse(atob(payload));
    const exp = decoded.exp;

    if (!exp) return true;

    // Check if expired or will expire within 60 seconds
    const now = Math.floor(Date.now() / 1000);
    return exp < now + 60;
  } catch {
    return true;
  }
}

async function getAuthHeader(): Promise<Record<string, string>> {
  console.log('[api] getAuthHeader: starting getSession...');

  // Add timeout to getSession to prevent hanging during auth state transitions
  let session: Awaited<ReturnType<typeof supabase.auth.getSession>>['data']['session'] = null;
  try {
    const getSessionPromise = supabase.auth.getSession();
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('getSession timeout')), 3000)
    );

    const result = await Promise.race([getSessionPromise, timeoutPromise]);
    session = result.data.session;
  } catch (error) {
    console.warn('[api] getAuthHeader: getSession timed out or failed:', error);
    return {};
  }
  console.log('[api] getAuthHeader: getSession complete, hasSession:', !!session);

  // If session exists but token is expired, try to refresh with timeout
  if (session?.access_token && isTokenExpired(session.access_token)) {
    console.log('[api] getAuthHeader: token expired, attempting refresh...');
    try {
      const refreshPromise = supabase.auth.refreshSession();
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Token refresh timeout')), 5000)
      );

      const { data: refreshData, error } = await Promise.race([
        refreshPromise,
        timeoutPromise,
      ]) as Awaited<ReturnType<typeof supabase.auth.refreshSession>>;

      if (!error && refreshData.session) {
        console.log('[api] getAuthHeader: token refreshed successfully');
        session = refreshData.session;
      } else {
        // Refresh failed - use existing token anyway, server will reject if invalid
        console.warn('[api] getAuthHeader: token refresh failed, using existing token');
      }
    } catch (error) {
      // Timeout or other error - use existing token anyway
      console.warn('[api] getAuthHeader: token refresh error:', error);
    }
  }

  if (session?.access_token) {
    console.log('[api] getAuthHeader: returning auth header');
    return {
      Authorization: `Bearer ${session.access_token}`,
    };
  }

  console.log('[api] getAuthHeader: no session, returning empty header');
  return {};
}

interface RequestOptions extends RequestInit {
  accessToken?: string;
}

async function request<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<ApiResponse<T>> {
  const { accessToken, ...fetchOptions } = options;

  let authHeader: Record<string, string> = {};

  // If access token is provided directly, use it (preferred - avoids getSession hang)
  if (accessToken) {
    console.log(`[api] request: ${fetchOptions.method || 'GET'} ${endpoint} - using provided token`);
    authHeader = { Authorization: `Bearer ${accessToken}` };
  } else {
    // Fallback to getSession (may hang during auth transitions)
    console.log(`[api] request: ${fetchOptions.method || 'GET'} ${endpoint} - getting auth header...`);
    authHeader = await getAuthHeader();
  }
  console.log(`[api] request: ${endpoint} - auth header obtained, making fetch...`);

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...fetchOptions,
    headers: {
      'Content-Type': 'application/json',
      ...authHeader,
      ...fetchOptions.headers,
    },
    credentials: 'include',
  });
  console.log(`[api] request: ${endpoint} - fetch complete, status: ${response.status}`);

  const data = await response.json();

  if (!response.ok) {
    console.log(`[api] request: ${endpoint} - error response:`, data);
    return {
      success: false,
      error: data.error || 'An unexpected error occurred',
      code: data.code,
    };
  }

  console.log(`[api] request: ${endpoint} - success`);
  return data as ApiResponse<T>;
}

export const api = {
  get: <T>(endpoint: string, accessToken?: string) =>
    request<T>(endpoint, { method: 'GET', accessToken }),

  post: <T>(endpoint: string, body?: unknown, accessToken?: string) =>
    request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
      accessToken,
    }),

  put: <T>(endpoint: string, body?: unknown, accessToken?: string) =>
    request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
      accessToken,
    }),

  patch: <T>(endpoint: string, body?: unknown, accessToken?: string) =>
    request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
      accessToken,
    }),

  delete: <T>(endpoint: string, accessToken?: string) =>
    request<T>(endpoint, { method: 'DELETE', accessToken }),
};
